import { ChevronRightIcon, DocumentDuplicateIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';

import DndDraggable from 'components/dnd/DndDraggable.component';
import Button from 'components/form/Button.component';
import { TogglerInput } from 'components/form/TogglerInput.component';
import { MobileBottomSheet } from 'components/generic/MobileBottomSheet.component';
import useIsMobileViewport from 'hooks/useIsMobileViewport.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';

import { QuestionTypeDropdown } from './QuestionTypeDropdown.component';

import CN from './QuestionCard.module.scss';

const DropdownItem = ({ icon, label, onClick, isRedCircle = false, rightIcon, testId }) => (
  <Button variant="empty" tag="QuestionCard__dropdownItem" data-testid={testId} className={CN.dropdownItem} onClick={onClick} width="auto" height="auto" noMargin noVerticalMargin noPadding>
    <div className={cn(CN.iconCircle, { [CN.iconCircleRed]: isRedCircle })}>{icon}</div>
    <span className={CN.dropdownItemText}>{label}</span>
    {rightIcon && <div className={CN.dropdownItemRightIcon}>{rightIcon}</div>}
  </Button>
);

export const QuestionCard = ({ question, index, onChangeType, onToggleRequired, onDuplicate, onDelete, onEditQuestionText }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const isMobile = useIsMobileViewport();
  const { val: isSheetOpen, tip: toggleSheet } = useTip(false);

  return (
    <DndDraggable tag="QuestionCard" draggableId={JSON.stringify(question.id)} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          data-bem="QuestionCard"
          data-testid={`QuestionCard-${index}`}
          className={cn(CN.component, {
            [CN.dragging]: snapshot.isDragging,
            [CN.darkMode]: isDarkMode,
          })}
        >
          <div className={CN.dragHandleAndQuestionNumber}>
            <div className={CN.dragHandle} {...provided.dragHandleProps}>
              <div className={cn(CN.dragDots, { [CN.dragging]: snapshot.isDragging })}>
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
            </div>

            <div className={CN.questionNumber}>{index + 1}</div>
          </div>

          <div className={CN.cardContent}>
            <div className={CN.topRow}>
              <div className={CN.questionTextContainer}>
                <div className={CN.questionTextTitleContainer}>
                  <span className={CN.questionText}>{question.text}</span>
                  <Button
                    variant="icon"
                    tag={`QuestionCard__editText-${question.id}`}
                    data-testid={`QuestionCard-${index}__editText`}
                    onClick={() => onEditQuestionText(index)}
                    width="auto"
                    height="auto"
                    noMargin
                    noVerticalMargin
                    noPadding
                  >
                    <PencilIcon className={CN.pencilIcon} />
                  </Button>
                </div>
                {question.description && <div className={CN.questionDescription}>{question.description}</div>}
              </div>
            </div>
            <div className={CN.bottomRow}>
              <QuestionTypeDropdown value={question.type} onChange={(type) => onChangeType(index, type)} />
              <div className={CN.requiredAndMobileActions}>
                <div className={CN.requiredToggle}>
                  <TogglerInput
                    checked={question.required}
                    onChange={() => onToggleRequired(index)}
                    data-testid={`QuestionCard-${index}__required`}
                    aria-label={t('consultationForms.builder.required')}
                  />

                  <span className={CN.requiredLabel}>{t('consultationForms.builder.required')}</span>
                </div>
                {isMobile && (
                  <>
                    <Button
                      variant="outlineForce"
                      tag={`QuestionCard__mobileActions-${question.id}`}
                      data-testid={`QuestionCard-${index}__mobileActions`}
                      onClick={toggleSheet}
                      width="auto"
                      noMargin
                      noVerticalMargin
                      className={CN.mobileEditButton}
                    >
                      <PencilIcon className={CN.pencilIcon} />
                    </Button>
                    <MobileBottomSheet isOpen={isSheetOpen} onClose={toggleSheet} contentClassName={cn(CN.mobileBottomSheetContent, { [CN.darkMode]: isDarkMode })}>
                      <DropdownItem
                        icon={<PencilIcon className={CN.sheetPencilIcon} />}
                        label={t('consultationForms.builder.edit')}
                        testId={`QuestionCard-${index}__mobileEdit`}
                        onClick={() => {
                          onEditQuestionText(index);
                          toggleSheet();
                        }}
                        rightIcon={<ChevronRightIcon className={CN.chevronRightIcon} />}
                      />
                      <DropdownItem
                        icon={<DocumentDuplicateIcon className={CN.sheetDuplicateIcon} />}
                        label={t('consultationForms.builder.duplicate')}
                        testId={`QuestionCard-${index}__mobileDuplicate`}
                        onClick={() => {
                          onDuplicate(index);
                          toggleSheet();
                        }}
                      />
                      <DropdownItem
                        icon={<TrashIcon className={CN.sheetTrashIcon} />}
                        label={t('consultationForms.builder.delete')}
                        testId={`QuestionCard-${index}__mobileDelete`}
                        onClick={() => {
                          onDelete(index);
                          toggleSheet();
                        }}
                        isRedCircle
                      />
                    </MobileBottomSheet>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className={CN.actions}>
            <Button
              variant="outlineForce"
              onClick={() => onDuplicate(index)}
              tag={`QuestionCard__duplicate-${question.id}`}
              data-testid={`QuestionCard-${index}__duplicate`}
              width="auto"
              noMargin
              noVerticalMargin
            >
              <DocumentDuplicateIcon className={CN.actionsDuplicateIcon} />
            </Button>
            <Button
              variant="alertOutline"
              onClick={() => onDelete(index)}
              tag={`QuestionCard__delete-${question.id}`}
              data-testid={`QuestionCard-${index}__delete`}
              width="auto"
              noMargin
              noVerticalMargin
            >
              <TrashIcon className={CN.actionsTrashIcon} />
            </Button>
          </div>
        </div>
      )}
    </DndDraggable>
  );
};
